/**
 * Şirket kurulumu (ADR 0042 §22–§24): Settings ▸ Company Setup sihirbazı, Roles, Users ve yetki ve
 * onay matrisleri editörü. Yalnız web ofis; gemide matrisler salt okunurdur.
 */
export const setupEn = {
  close: 'Close',
  card: {
    title: 'Company Setup',
    text: 'Vessels, roles, the authorization and approval matrices, users and setup codes — step by step.',
    progress: '{{done}} of {{total}} steps complete',
    open: 'Open company setup',
  },
  wizard: {
    title: 'Company Setup',
    description:
      'Work through the steps in any order. Each step opens the screen where the data is kept; progress is read from your data.',
    crumb: 'Company Setup',
    steps: 'Setup steps',
    open: 'Open',
    continue: 'Continue: {{step}}',
    complete: 'Setup is complete',
    progressTitle: 'Progress',
    progress: '{{done}} of {{total}} steps complete',
    matrix: 'Matrix version',
    matrixValue: 'v{{version}}',
    warningsTitle: 'Attention',
    noWarnings: 'Nothing needs attention.',
    loadError: 'The setup status could not be loaded.',
    status: {
      done: 'Done',
      todo: 'To do',
      default: 'Default',
      customized: 'Customized (v{{version}})',
    },
    step: {
      company: {
        title: 'Company details',
        text: 'Brand name, logo and base currency for approval amount bands.',
        detail: '{{name}} · {{currency}}',
        missing: 'No company name yet',
      },
      vessels: {
        title: 'Vessels',
        text: 'Add the fleet; each vessel gets its document template.',
        detail_one: '{{count}} vessel',
        detail_other: '{{count}} vessels',
      },
      roles: {
        title: 'Roles',
        text: 'The eight default roles, plus any role your company needs.',
        detail_one: '{{count}} role, {{custom}} added by the company',
        detail_other: '{{count}} roles, {{custom}} added by the company',
      },
      permissions: {
        title: 'Authorization matrix',
        text: 'Who may view, create, edit, submit, approve, return, void and export, per module.',
        detail: 'Current version v{{version}}',
      },
      library: {
        title: 'Document library',
        text: 'Form Categories of each vessel and optional company documents.',
        detail: '{{withTree}} of {{vessels}} vessels have a tree · {{documents}} company documents',
      },
      approvals: {
        title: 'Approval matrix',
        text: 'Approval steps per record type and ISM form category.',
        detail: 'Current version v{{version}}',
      },
      users: {
        title: 'Users and vessel assignments',
        text: 'Office users, company roles and dated vessel assignments.',
        detail: '{{active}} active users · {{manned}} of {{vessels}} vessels have a master',
      },
      setupCodes: {
        title: 'Setup codes',
        text: 'Install the ship server on each vessel with a one-time setup code.',
        detail: '{{activated}} of {{vessels}} vessels activated',
      },
    },
    warning: {
      COMPANY_NAME_MISSING: 'The company name is not set.',
      NO_VESSELS: 'There are no vessels yet.',
      NO_MASTER:
        '{{vessel}} has no active user assigned as Master: approvals on this vessel cannot be given.',
    },
  },
  roles: {
    title: 'Roles',
    description:
      'Roles are used in the authorization and approval matrices and in company and vessel assignments.',
    crumb: 'Roles',
    table: 'Roles',
    search: 'Search roles',
    new: 'New Role',
    edit: 'Edit',
    retire: 'Retire',
    actionsOf: 'Actions for {{name}}',
    default: 'Default',
    loadError: 'The roles could not be loaded.',
    emptyTitle: 'No roles yet',
    emptyText: 'Add the roles your company uses.',
    noMatch: 'No role matches the search.',
    pagination: 'Roles pages',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    columns: {
      name: 'Role',
      description: 'Description',
      pms: 'PMS responsible',
      users: 'People',
      kind: 'Type',
      status: 'Status',
      actions: 'Actions',
    },
    yes: 'Yes',
    no: 'No',
    // Rol türü (ADR 0143)
    kinds: {
      vessel: 'Vessel rank',
      office: 'Office title',
      any: 'Vessel and office',
    },
    inUse: 'In use',
    unused: 'Not used',
    dialog: {
      newTitle: 'New Role',
      editTitle: 'Edit Role',
      name: 'Name',
      nameLocked: 'Default role names cannot be changed; the translated name is shown in the app.',
      description: 'Description',
      pms: 'PMS responsible',
      pmsHelp:
        'The role that keeps the planned maintenance programme (chief engineer). Other roles need a justification for maintenance programme rights.',
      kind: 'Type',
      kindHelp:
        'Vessel ranks are offered at Sign On and Rank change; office titles as company roles in Users. "Vessel and office" is offered in both. Existing assignments are not changed.',
      kindLocked:
        'The type of a default role cannot be changed: it decides where the role is given. Add a new role for another type.',
      kindRestore:
        'The type of this default role was changed earlier. It can only be set back to its system type: {{kind}}.',
      kindAdminOnly: 'Only a company administrator can change the type of an administrator role.',
      save: 'Save',
      cancel: 'Cancel',
    },
    retireDialog: {
      title: 'Retire role',
      body: '{{name}} will no longer be offered in matrices and assignments. Past records keep the role name.',
      confirm: 'Retire',
    },
    saved: '{{name}} saved.',
    retired: '{{name}} retired.',
    names: {
      master: 'Master',
      chief_officer: 'Chief Officer',
      chief_engineer: 'Chief Engineer',
      engineer_officer: 'Engineer Officer',
      office_manager: 'Office Manager',
      superintendent: 'Superintendent',
      company_admin: 'Company Administrator',
      dpa: 'Designated Person Ashore',
      // Ofis ünvanları (ADR 0139 §3, ADR 0143 §5): ad değiştirilmemişse çevrilir
      technical_manager: 'Technical Manager',
      marine_superintendent: 'Marine Superintendent',
      technical_superintendent: 'Technical Superintendent',
      deck_superintendent: 'Deck Superintendent',
      operation_manager: 'Operation Manager',
      crew_manager: 'Crew Manager',
      assistant_crew_manager: 'Assistant Crew Manager',
    },
    descriptions: {
      master: 'Master: overall command on board; approves vessel records (ISM Code 5).',
      chief_officer: 'Deck department head; enters and submits deck records.',
      chief_engineer: 'Engine department head; keeps the maintenance programme (UR Z20).',
      engineer_officer: 'Engineer officer; enters and submits engine records.',
      office_manager: 'Office; decides office approval steps and manages users.',
      superintendent: 'Technical superintendent; fleet view and office approvals.',
      company_admin: 'Company setup: settings, users and the matrices.',
      dpa: 'Designated Person Ashore (ISM Code 4); views and audits, does not change records.',
    },
  },
  users: {
    title: 'Users',
    description:
      'Office users and crew. Sign-in is by e-mail; vessel rights come only from dated vessel assignments.',
    crumb: 'Users',
    table: 'Users',
    search: 'Search name or e-mail',
    status: 'Status',
    statusAll: 'All',
    statusActive: 'Active',
    statusDisabled: 'Disabled',
    new: 'New User',
    loadError: 'The users could not be loaded.',
    emptyTitle: 'No users yet',
    emptyText: 'Add the people who will sign in to the office or on board.',
    noMatch: 'No user matches the filters.',
    pagination: 'Users pages',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    actionsOf: 'Actions for {{name}}',
    columns: {
      name: 'Name',
      companyRoles: 'Company roles',
      assignments: 'Vessel assignments',
      signIn: 'Sign-in',
      status: 'Status',
      actions: 'Actions',
    },
    active: 'Active',
    disabled: 'Disabled',
    admin: 'Administrator',
    // İki adımlı doğrulama isteğe bağlı (sahip 2026-11-01, ADR 0148): kapalı olması uyarı değil
    totpSet: 'Two-step on',
    totpMissing: 'Two-step off',
    mustChange: 'Must change password',
    none: '—',
    assignment: '{{role}} · {{vessel}}',
    more: '+{{count}} more',
    action: {
      edit: 'Edit',
      changeEmail: 'Change login email',
      resetPassword: 'Reset password',
      // Yönetici kişinin iki adımlı doğrulamasını kapatır (ADR 0148); kişi isterse yeniden açar
      resetTotp: 'Turn off two-step verification',
      disable: 'Disable',
      enable: 'Enable',
    },
    confirm: {
      password: 'Your password',
      passwordHelp: 'Enter your own password to confirm.',
      cancel: 'Cancel',
      createTitle: 'Create user',
      createBody: 'Create {{email}}? A temporary password is shown once after creation.',
      create: 'Create user',
      disableTitle: 'Disable user',
      disableBody:
        '{{name}} is signed out now and can no longer sign in. Vessels receive the change with the next synchronisation.',
      disable: 'Disable',
      enableTitle: 'Enable user',
      enableBody:
        '{{name}} can sign in again. Vessels receive the change with the next synchronisation.',
      enable: 'Enable',
      resetPasswordTitle: 'Reset password',
      resetPasswordBody:
        '{{name}} gets a new temporary password and must change it at the next sign-in. A password changed on board stops working; the new password works on board after the next sync.',
      resetPassword: 'Reset password',
      resetTotpTitle: 'Turn off two-step verification',
      resetTotpBody:
        '{{name}} then signs in to the office with the password only and can turn two-step verification on again under Settings ▸ Account.',
      resetTotp: 'Turn off',
    },
    temp: {
      title: 'Temporary password',
      body: 'Give this to {{name}} by phone or WhatsApp. It is shown only once.',
      label: 'Temporary password',
      copy: 'Copy password',
      copied: 'Copied',
      // Hazır mesaj ve giriş kâğıdı (ortak 2026-10-30 madde 9): iki dilde, e-posta servisi yok
      copyMessage: 'Copy message',
      messageCopied: 'Message copied',
      print: 'Print',
      sheetTitle: 'Felixmate login',
      message: {
        hello: 'Hello {{name}},',
        ready: 'Your Felixmate login is ready.',
        office: 'Office: {{url}}',
        ship: "On board: use the Felixmate app on the ship's computer.",
        email: 'Login e-mail: {{email}}',
        password: 'Temporary password: {{password}}',
        change: 'Change it at first sign-in.',
      },
      note: 'The user must change it at the first sign-in. In the office, two-step verification is optional: it can be turned on under Settings ▸ Account.',
      // Sıfırlama devre dışı kişiyi etkinleştirmez (ADR 0150 §5)
      inactive:
        '{{name}} is disabled: the new password cannot be used until the account is enabled.',
      done: 'Done',
    },
    form: {
      newTitle: 'New User',
      email: 'E-mail',
      emailHelp: 'Used for sign-in on board and in the office; unique across Felixmate.',
      fullName: 'Full name',
      companyRoles: 'Company roles',
      companyRolesHelp: 'Office roles, with dates.',
      assignments: 'Vessel assignments',
      assignmentsHelp: 'Rights on board come only from these dated assignments.',
      role: 'Role',
      vessel: 'Vessel',
      from: 'From',
      to: 'To',
      toOpen: 'Open-ended',
      addRole: 'Add company role',
      addAssignment: 'Add vessel assignment',
      remove: 'Remove',
      save: 'Save',
      cancel: 'Cancel',
      next: 'Continue',
      required: 'Required.',
      // Kontrat bitişi (ADR 0076) [MLC 2006 Std A2.5.1 §2(b)]
      contractEnd: 'Contract end',
      contractOpen: 'Not entered',
      contractHint: 'MLC 12-month limit on board: {{date}}. A warning appears 60 days before.',
      contractHintEmpty: 'Enter the joining date to see the MLC 12-month limit.',
      contractReason: 'Reason for exceeding the MLC limit',
      contractExceeds:
        'The contract ends after the MLC 12-month limit ({{date}}). Record the reason, e.g. a flag State dispensation.',
      contractReasonRequired: 'A reason is required when the contract exceeds the MLC limit.',
    },
    drawer: {
      title: 'Edit User',
      details: 'Details',
      saveDetails: 'Save details',
      // Kişinin personel kartı (ADR 0131 §2)
      openCrewCard: 'Open crew card',
      // Kartlı kişi: Sign On / Off işlemleri çekmeceden (ADR 0155 Ek, sahip 2026-11-02 gece)
      assignmentsByCrew:
        'This person has a crew card. Vessel roles change with Sign On / Off (Sign on, Rank change, Sign off); each change is kept in the MLC employment record.',
      manageSignOnOff: 'Manage in Sign On / Off',
      noAssignments: 'No vessel assignment yet.',
      crewLoadError: 'The crew card could not be read ({{code}}).',
      saveRoles: 'Save company roles',
      saveAssignments: 'Save vessel assignments',
      // İyimser kilit (ADR 0155 K7)
      assignmentsConflict:
        'The vessel assignments changed in the meantime (company panel, Sign On / Off or another user). Nothing was saved. Close and reopen the user to see the current rows.',
      // Yönetici olmayana salt okunur bölümler (U39; ADR 0151 §3)
      emailAdminOnly: 'Only a company administrator can change an administrator’s login email.',
      rolesAdminOnly:
        'These roles include an administrator role. Only a company administrator can change them.',
      rolesOwn: 'You cannot change your own roles. Ask a company administrator.',
      assignmentsOwn: 'You cannot change your own vessel assignments. Ask a company administrator.',
      assignmentAdminOnly:
        'This assignment has an administrator role. Only a company administrator can change or remove it.',
      // Yeni satır users.create (ADR 0151 Ek)
      addRoleNeedsCreate: 'Adding a company role needs Users ▸ Create.',
      addAssignmentNeedsCreate:
        'Adding a vessel assignment or moving one to another vessel needs Users ▸ Create.',
      // Gemi kapsamlı yönetici şirket rolü (ADR 0150 §1)
      assignmentsAdminScope:
        'This person’s company role gives administrator rights on the vessels they are assigned to. Only a company administrator can change their vessel assignments.',
      account: 'Account',
      close: 'Close',
    },
    // Satır menüsünde eksik işlemin nedeni (ADR 0151 §3)
    menuNote: {
      adminAccount: 'Only a company administrator can change an administrator’s account.',
      ownAccount: 'You cannot disable your own account.',
    },
    saved: '{{name}} saved.',
    created: '{{name}} created.',
    disabledNotice: '{{name}} disabled; sessions closed.',
    enabledNotice: '{{name}} enabled.',
    totpNotice: 'Two-step verification turned off for {{name}}.',
    // Yönetici hesabına geçişte iptal edilen geçici parola (ADR 0150 §6 sınır c)
    temporaryPasswordDropped_one:
      'Temporary password cancelled for {{names}}: the account now holds an administrator role. Give a new temporary password with “{{action}}”.',
    temporaryPasswordDropped_other:
      'Temporary passwords cancelled for {{names}}: the accounts now hold an administrator role. Give new temporary passwords with “{{action}}”.',
    // Settings ▸ Users kartı ve yetkisiz kişi (ortak 2026-10-30)
    card: {
      title: 'Users',
      text: 'Office users and crew logins: create accounts, correct login emails, reset passwords and turn off two-step verification.',
      open: 'Open users',
    },
    forbiddenTitle: 'You do not have access to users',
    forbiddenText: 'Ask your company administrator for the users view right.',
    // Giriş e-postasını düzeltme (ADR 0145)
    changeEmail: {
      title: 'Change login email',
      body: 'Correct the address {{name}} signs in with. Records and history stay with the same person; the old address becomes free. Vessels receive the change with the next synchronisation.',
      current: 'Current login email',
      next: 'New login email',
      nextHelp: 'Unique across Felixmate.',
      submit: 'Change email',
      notice:
        'Login email of {{name}} changed to {{email}}. Tell them to sign in with the new address.',
    },
  },
  matrix: {
    title: 'Authorization Matrices',
    description:
      'Authorization and approval matrices are saved together as a new version with a reason and your password. Earlier records keep the version they were entered with.',
    crumb: 'Matrices',
    tabs: { permissions: 'Permissions', approvals: 'Approvals', history: 'Version history' },
    loadError: 'The matrices could not be loaded.',
    readOnly: 'You can view the matrices. Changing them needs settings edit rights.',
    modules: 'Modules',
    moduleGranted_one: '{{count}} grant',
    moduleGranted_other: '{{count}} grants',
    grid: 'Permissions for {{module}}',
    gridHelp:
      'Click a cell, or use the arrow keys and Space, to change the scope: none → own → vessel → fleet.',
    role: 'Role',
    subject: 'Subject',
    subjectAll: 'All forms',
    addSubject: 'Add subject',
    removeSubject: 'Remove subject',
    subjectHelp:
      'A subject row overrides the module row for one form category or one form; "none" closes it.',
    locked: 'Not grantable: {{source}}',
    justified: 'Needs a justification',
    cellLabel: '{{role}}, {{action}}: {{scope}}',
    scope: { none: 'None', own: 'Own', vessel: 'Vessel', fleet: 'Fleet' },
    scopeShort: { none: '—', own: 'Own', vessel: 'Vessel', fleet: 'Fleet' },
    legend: 'Scope legend',
    actions: {
      view: 'View',
      create: 'Create',
      edit: 'Edit',
      submit: 'Submit',
      approve: 'Approve',
      return: 'Return',
      void: 'Void',
      export: 'Export',
    },
    moduleNames: {
      ism_forms: 'ISM forms',
      equipment: 'Equipment',
      maintenance_program: 'Maintenance programme',
      work_records: 'Work records',
      running_hours: 'Running hours',
      postponements: 'Postponements',
      defects: 'Defects',
      work_orders: 'Work orders',
      vessel_certificates: 'Vessel certificates',
      reports: 'Reports',
      users: 'Users',
      devices: 'Devices',
      settings: 'Settings',
      crew_certificates: 'Crew certificates',
      rest_hours: 'Hours of rest',
      voyage: 'Voyage',
      documents: 'Documents',
      document_acknowledgements: 'Document acknowledgements',
      document_tree: 'Document tree',
      materials: 'Materials',
      stock: 'Stock',
      purchasing: 'Purchasing',
      hse_reports: 'HSE reports',
      daily_works: 'Daily works plan',
      daily_reports: 'Daily reports',
      risk_assessments: 'Risk assessments',
      payroll: 'Payroll',
      cash: 'Master cash & CTM',
      provisions: 'Provisions',
      fuel: 'Fuel management',
      mrv: 'MRV / ETS',
      drills: 'Drill management',
      drydock: 'Dry docking and pre-docking inspection',
      crew_medical: 'Crew medical information',
      hse_medical: 'HSE medical information',
      crew_notes: 'Crew office notes',
      company_nonconformities: 'Company non-conformities',
      ship_clock: "Ship's clock log",
      ra_library: 'Risk assessment library',
      ra_operations: 'Operational risk verification',
    },
    recordTypes: {
      rest_hours_month: 'Monthly hours of rest',
      voyage_report: 'Voyage report',
      requisition: 'Material requisition',
      document_revision: 'Document revision',
      ism_form: 'ISM form',
      vessel_document_revision: 'Vessel document revision',
      pms_work_record: 'PMS work record',
      postponement_request: 'Postponement request',
      maintenance_interval_change: 'Maintenance interval change',
      defect_report: 'Defect report',
      breakdown_report: 'Breakdown report',
      hse_non_conformity: 'Non-conformity closure',
      hse_deficiency: 'Deficiency closure',
      hse_near_miss: 'Near miss closure',
      hse_accident: 'Accident closure',
      daily_report: 'Daily report',
      risk_assessment: 'Risk assessment',
      ctm_request: 'Cash to Master request',
    },
    summary: {
      title: 'This Version',
      base: 'Based on',
      baseValue: 'v{{version}} · {{at}}',
      by: 'Saved by',
      system: 'Felixmate (default)',
      reason: 'Reason',
      changes: 'Unsaved changes',
      changesValue_one: '{{count}} change',
      changesValue_other: '{{count}} changes',
      noChanges: 'No changes',
      save: 'Save New Version',
      discard: 'Discard changes',
      export: 'Export Excel',
      import: 'Import Excel',
    },
    justification: {
      title: 'Justification required',
      body: '{{role}} is not the PMS responsible role. Granting "{{action}}" on the maintenance programme needs a justification [UR Z20 1.3.3].',
      label: 'Justification',
      save: 'Grant',
      cancel: 'Cancel',
    },
    subjectDialog: {
      title: 'Add subject',
      kind: 'Subject type',
      folder: 'Form category',
      document: 'Single form',
      folderLabel: 'Category',
      documentLabel: 'Form',
      companyForm: 'Company form',
      add: 'Add',
      cancel: 'Cancel',
      empty: 'No categories or forms to choose from.',
    },
    rules: {
      table: 'Approval rules',
      help: 'The rule is chosen by record type, ISM form category and the role that enters the record; at most two steps.',
      add: 'Add Rule',
      edit: 'Edit',
      remove: 'Remove',
      actionsOf: 'Actions for rule {{index}}',
      empty: 'No approval rules.',
      columns: {
        recordType: 'Record type',
        subject: 'Subject',
        enteredBy: 'Entered by',
        step1: 'Step 1',
        step2: 'Step 2',
        amount: 'Amount band',
        deadline: 'Deadline',
        selfApproval: 'Self-approval',
        actions: 'Actions',
      },
      any: 'Any role',
      office: 'Office',
      noStep: 'No approval',
      hours: '{{count}} h',
      from: 'from {{amount}}',
      to: 'below {{amount}}',
      dialog: {
        newTitle: 'Add Approval Rule',
        editTitle: 'Edit Approval Rule',
        recordType: 'Record type',
        subject: 'Subject (ISM forms only)',
        subjectNone: 'All ISM forms',
        enteredBy: 'Entered by',
        anyRole: 'Any role',
        step1: 'Step 1 approver',
        step2: 'Step 2 approver',
        noStep: 'None',
        office: 'Decided in the office',
        minAmount: 'From amount',
        maxAmount: 'Below amount',
        deadline: 'Deadline (hours)',
        selfApproval: 'Self-approval reason',
        selfApprovalHelp: 'Only if the same person may enter and approve; shown in reports.',
        save: 'Save',
        cancel: 'Cancel',
      },
    },
    history: {
      table: 'Matrix versions',
      columns: {
        version: 'Version',
        at: 'Saved',
        by: 'Saved by',
        reason: 'Reason',
        compare: 'Compare',
      },
      compare: 'Compare with previous',
      from: 'From version',
      to: 'To version',
      show: 'Show differences',
    },
    diff: {
      title: 'Differences',
      permissions: 'Permission cells',
      approvals: 'Approval rules',
      justifications: 'Justifications',
      added: 'Added',
      removed: 'Removed',
      changed: 'Changed',
      none: 'No differences.',
      change: 'Change',
      fromTo: '{{from}} → {{to}}',
      summary: '{{added}} added, {{removed}} removed, {{changed}} changed',
    },
    save: {
      title: 'Save new matrix version',
      body: 'Both matrices are saved as version {{version}}. Vessels receive it with the next synchronisation.',
      reason: 'Reason for the change',
      password: 'Your password',
      confirm: 'Save Version',
      cancel: 'Cancel',
      conflict:
        'Someone saved a newer version while you were editing. Reload it and apply your changes again.',
      reload: 'Reload latest version',
      saved: 'Version {{version}} saved.',
      // Rolü yönetici rolü olan kişilerin iptal edilen geçici parolaları (ADR 0150 §6 sınır c)
      temporaryPasswordDropped_one:
        'The temporary password of {{count}} person was cancelled: their role is now an administrator role. Give a new one in Settings ▸ Users with “{{action}}”.',
      temporaryPasswordDropped_other:
        'The temporary passwords of {{count}} people were cancelled: their roles are now administrator roles. Give new ones in Settings ▸ Users with “{{action}}”.',
    },
    import: {
      title: 'Import from Excel',
      choose: 'Choose an .xlsx file exported from this screen.',
      file: 'Excel file',
      errors: 'The file has errors; nothing was changed.',
      errorRow: '{{sheet}} row {{row}}, {{field}}: {{code}}',
      problem: 'The imported matrix breaks a rule: {{code}}.',
      ready:
        'Review the differences. The imported matrix replaces your draft; save it as a new version with a reason.',
      use: 'Use imported matrix',
      cancel: 'Cancel',
      sheets: {
        permissions: 'Permissions',
        approvals: 'Approvals',
        justifications: 'Justifications',
      },
    },
  },
  errors: {
    FORBIDDEN: 'You are not authorised for this action.',
    UNAUTHENTICATED: 'Your session has ended. Sign in again.',
    NETWORK: 'No connection to the server.',
    VALIDATION_FAILED: 'Some fields are invalid.',
    REAUTHENTICATION_REQUIRED: 'Enter your password.',
    INVALID_PASSWORD: 'The password is not correct.',
    TOO_MANY_ATTEMPTS: 'Too many attempts. Try again in 15 minutes.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reload and try again.',
    NOT_FOUND: 'The record was not found.',
    DEFAULT_ROLE_LOCKED: 'Default roles cannot be renamed, given another type or retired.',
    DUPLICATE_ROLE_NAME: 'A role with this name already exists.',
    ROLE_IN_USE: 'The role is used in the current matrix or in an assignment.',
    ROLE_NAME_REQUIRED: 'Enter a role name.',
    ROLE_NAME_TOO_LONG: 'The role name is too long (80 characters at most).',
    ROLE_NAME_INVALID: 'The role name cannot contain ";".',
    ROLE_DESCRIPTION_TOO_LONG: 'The description is too long.',
    ROLE_KIND_MISMATCH:
      'A vessel rank cannot be a company role. Choose an office title or change the role type in Roles.',
    EMAIL_TAKEN: 'This e-mail address is already used in Felixmate.',
    INVALID_EMAIL: 'Enter a valid e-mail address.',
    ASSIGNMENT_OVERLAP: 'Two assignments overlap on the same vessel (or the same company role).',
    INVALID_DATE_RANGE: 'The end date is before the start date.',
    CONTRACT_EXCEEDS_MLC:
      'The contract end is after the MLC 12-month limit on board. Enter a reason to save it.',
    INVALID_LOCAL_DATE: 'Enter a valid date.',
    UNKNOWN_ROLE: 'The role does not exist.',
    UNKNOWN_VESSEL: 'The vessel does not exist.',
    LAST_ADMIN: 'At least one active administrator must remain, today and on every later day.',
    LAST_ADMIN_NO_SIGN_IN:
      'No administrator who can sign in would remain: the other administrators have no login or no password (a temporary password is cancelled when the person becomes an administrator). Give one of them a new password first (or, if they have no login, create one in Crew ▸ Login Accounts), then try again.',
    LAST_ADMIN_SELF: 'You cannot disable your own account.',
    INVALID_TRANSITION: 'The account is already in this state.',
    TOTP_NOT_ENROLLED: 'Two-step verification is already off for this user.',
    MATRIX_VERSION_CONFLICT: 'A newer version was saved meanwhile.',
    MATRIX_LOCKOUT:
      'On every day from today at least one active user must keep the "Settings – Edit (Fleet)" right; otherwise nobody could change the setup again.',
    MATRIX_LOCKOUT_NO_SIGN_IN:
      'No administrator who can sign in would keep "Settings – Edit (Fleet)": the people who would keep it have no login or no password (saving cancels the temporary password of people who become administrators). Keep your own right in this version, give them a new password (or, if they have no login, create one in Crew ▸ Login Accounts), then save the change again.',
    NO_CHANGE: 'Nothing has changed.',
    REASON_REQUIRED: 'Enter a reason (5 characters at least).',
    REASON_TOO_LONG: 'The reason is too long.',
    NON_GRANTABLE_CELL: 'A cell that cannot be granted is set.',
    JUSTIFICATION_REQUIRED: 'A maintenance programme right needs a justification.',
    TOO_MANY_APPROVAL_STEPS: 'An approval rule has at most two steps.',
    DUPLICATE_RULE: 'Two rules apply to the same record type and roles.',
    OVERLAPPING_AMOUNT_BANDS: 'Two amount bands overlap.',
    AMOUNT_BAND_GAP: 'The "any role" amount bands must cover every amount.',
    INVALID_AMOUNT_BAND: 'The amount band is invalid.',
    INVALID_DEADLINE: 'The deadline must be a positive number of hours.',
    INVALID_OFFICE_STEP: 'An office step needs an approver.',
    FORM_OFFICE_STEP_NOT_ALLOWED:
      'ISM forms do not go to the office for approval. Remove the office step.',
    SELF_APPROVAL_REASON_REQUIRED: 'Enter the self-approval reason.',
    ENTERED_BY_REQUIRED: 'Choose who enters the record.',
    SUBJECT_ONLY_FOR_FORMS: 'A subject is only possible for ISM forms.',
    INVALID_SUBJECT: 'The subject is invalid.',
    UNKNOWN_RECORD_TYPE: 'Unknown record type.',
    DUPLICATE_CELL: 'A cell is listed twice.',
    INVALID_FILE: 'The file could not be read as an Excel workbook.',
    SHEET_MISSING: 'The workbook has no Permissions or Approvals sheet.',
    INVALID_HEADER: 'The column headings were changed; export a fresh file.',
    UNSUPPORTED_FILE_TYPE: 'Only .xlsx files without macros or active content are accepted.',
    FILE_TOO_LARGE: 'The file is too large (10 MB at most).',
    FILE_TYPE_MISMATCH: 'The file content does not match .xlsx.',
    GENERIC: 'The operation failed. Try again.',
  },
};

export type SetupResources = typeof setupEn;

export const setupTr: SetupResources = {
  close: 'Kapat',
  card: {
    title: 'Şirket Kurulumu',
    text: 'Gemiler, roller, yetki ve onay matrisleri, kullanıcılar ve kurulum kodları — adım adım.',
    progress: '{{total}} adımdan {{done}} tamam',
    open: 'Şirket kurulumunu aç',
  },
  wizard: {
    title: 'Şirket Kurulumu',
    description:
      'Adımları istediğiniz sırayla yürütün. Her adım verinin tutulduğu ekranı açar; ilerleme verinizden okunur.',
    crumb: 'Şirket Kurulumu',
    steps: 'Kurulum adımları',
    open: 'Aç',
    continue: 'Devam: {{step}}',
    complete: 'Kurulum tamam',
    progressTitle: 'İlerleme',
    progress: '{{total}} adımdan {{done}} tamam',
    matrix: 'Matris sürümü',
    matrixValue: 'v{{version}}',
    warningsTitle: 'Dikkat',
    noWarnings: 'Dikkat gerektiren bir şey yok.',
    loadError: 'Kurulum durumu yüklenemedi.',
    status: {
      done: 'Tamam',
      todo: 'Yapılacak',
      default: 'Varsayılan',
      customized: 'Değiştirildi (v{{version}})',
    },
    step: {
      company: {
        title: 'Şirket bilgileri',
        text: 'Marka adı, logo ve onay tutar kademelerinin temel para birimi.',
        detail: '{{name}} · {{currency}}',
        missing: 'Şirket adı henüz yok',
      },
      vessels: {
        title: 'Gemiler',
        text: 'Filoyu ekleyin; her gemi doküman şablonunu alır.',
        detail_one: '{{count}} gemi',
        detail_other: '{{count}} gemi',
      },
      roles: {
        title: 'Roller',
        text: 'Sekiz varsayılan rol ve şirketinizin gerek duyduğu roller.',
        detail_one: '{{count}} rol, {{custom}} tanesi şirketin',
        detail_other: '{{count}} rol, {{custom}} tanesi şirketin',
      },
      permissions: {
        title: 'Yetki matrisi',
        text: 'Modül başına kim görür, oluşturur, düzenler, gönderir, onaylar, iade eder, iptal eder ve dışa aktarır.',
        detail: 'Güncel sürüm v{{version}}',
      },
      library: {
        title: 'Doküman kütüphanesi',
        text: 'Her geminin Form Kategorileri ve isteğe bağlı şirket belgeleri.',
        detail:
          '{{vessels}} gemiden {{withTree}} tanesinin ağacı var · {{documents}} şirket belgesi',
      },
      approvals: {
        title: 'Onay matrisi',
        text: 'Kayıt türü ve ISM form kategorisi başına onay adımları.',
        detail: 'Güncel sürüm v{{version}}',
      },
      users: {
        title: 'Kullanıcılar ve gemi atamaları',
        text: 'Ofis kullanıcıları, şirket rolleri ve tarihli gemi atamaları.',
        detail: '{{active}} etkin kullanıcı · {{vessels}} gemiden {{manned}} tanesinin kaptanı var',
      },
      setupCodes: {
        title: 'Kurulum kodları',
        text: 'Her gemide gemi sunucusunu tek kullanımlık kurulum koduyla kurun.',
        detail: '{{vessels}} gemiden {{activated}} tanesi etkin',
      },
    },
    warning: {
      COMPANY_NAME_MISSING: 'Şirket adı girilmemiş.',
      NO_VESSELS: 'Henüz gemi yok.',
      NO_MASTER:
        '{{vessel}} için kaptan olarak atanmış etkin kullanıcı yok: bu gemide onay verilemez.',
    },
  },
  roles: {
    title: 'Roller',
    description:
      'Roller yetki ve onay matrislerinde, şirket rollerinde ve gemi atamalarında kullanılır.',
    crumb: 'Roller',
    table: 'Roller',
    search: 'Rol ara',
    new: 'Yeni Rol',
    edit: 'Düzenle',
    retire: 'Emekli et',
    actionsOf: '{{name}} için eylemler',
    default: 'Varsayılan',
    loadError: 'Roller yüklenemedi.',
    emptyTitle: 'Henüz rol yok',
    emptyText: 'Şirketinizin kullandığı rolleri ekleyin.',
    noMatch: 'Aramaya uyan rol yok.',
    pagination: 'Rol sayfaları',
    showing: '{{total}} kayıttan {{from}}–{{to}}',
    columns: {
      name: 'Rol',
      description: 'Açıklama',
      pms: 'PMS sorumlusu',
      users: 'Kişi',
      kind: 'Tür',
      status: 'Durum',
      actions: 'Eylemler',
    },
    yes: 'Evet',
    no: 'Hayır',
    kinds: {
      vessel: 'Gemi rütbesi',
      office: 'Ofis ünvanı',
      any: 'Gemi ve ofis',
    },
    inUse: 'Kullanımda',
    unused: 'Kullanılmıyor',
    dialog: {
      newTitle: 'Yeni Rol',
      editTitle: 'Rolü Düzenle',
      name: 'Ad',
      nameLocked: 'Varsayılan rollerin adı değiştirilemez; uygulamada çevrilmiş adı görünür.',
      description: 'Açıklama',
      pms: 'PMS sorumlusu',
      pmsHelp:
        'Planlı bakım programını tutan rol (başmühendis). Diğer rollere bakım programı yetkisi gerekçeyle verilir.',
      kind: 'Tür',
      kindHelp:
        'Gemi rütbeleri Katılış ve Rütbe değişikliğinde, ofis ünvanları Kullanıcılar’da şirket rolü olarak sunulur. "Gemi ve ofis" ikisinde de sunulur. Var olan atamalar değişmez.',
      kindLocked:
        'Varsayılan rolün türü değiştirilemez: tür rolün nerede verileceğini belirler. Başka bir tür için yeni rol ekleyin.',
      kindRestore:
        'Bu varsayılan rolün türü daha önce değiştirilmiş. Yalnız sistem türüne geri alınabilir: {{kind}}.',
      kindAdminOnly: 'Yönetici rolünün türünü yalnız şirket yöneticisi değiştirebilir.',
      save: 'Kaydet',
      cancel: 'Vazgeç',
    },
    retireDialog: {
      title: 'Rolü emekli et',
      body: '{{name}} artık matrislerde ve atamalarda sunulmaz. Geçmiş kayıtlar rol adını korur.',
      confirm: 'Emekli et',
    },
    saved: '{{name}} kaydedildi.',
    retired: '{{name}} emekli edildi.',
    names: {
      master: 'Kaptan',
      chief_officer: 'Birinci Zabit',
      chief_engineer: 'Başmühendis',
      engineer_officer: 'Makine Zabiti',
      office_manager: 'Ofis Müdürü',
      superintendent: 'Süperintendent',
      company_admin: 'Şirket Yöneticisi',
      dpa: 'Karadaki Belirlenmiş Kişi (DPA)',
      technical_manager: 'Teknik Müdür',
      marine_superintendent: 'Denizcilik Süperintendenti',
      technical_superintendent: 'Teknik Süperintendent',
      deck_superintendent: 'Güverte Süperintendenti',
      operation_manager: 'Operasyon Müdürü',
      crew_manager: 'Personel Müdürü',
      assistant_crew_manager: 'Personel Müdür Yardımcısı',
    },
    descriptions: {
      master: 'Kaptan: gemide genel komuta; gemi kayıtlarını onaylar (ISM Kodu 5).',
      chief_officer: 'Güverte bölüm şefi; güverte kayıtlarını girer ve gönderir.',
      chief_engineer: 'Makine bölüm şefi; bakım programını tutar (UR Z20).',
      engineer_officer: 'Makine zabiti; makine kayıtlarını girer ve gönderir.',
      office_manager: 'Ofis; ofis onay adımlarına karar verir ve kullanıcıları yönetir.',
      superintendent: 'Teknik süperintendent; filoyu görür, ofis onaylarını verir.',
      company_admin: 'Şirket kurulumu: ayarlar, kullanıcılar ve matrisler.',
      dpa: 'Karadaki belirlenmiş kişi (ISM Kodu 4); görür ve denetler, kayıt değiştirmez.',
    },
  },
  users: {
    title: 'Kullanıcılar',
    description:
      'Ofis kullanıcıları ve gemi personeli. Giriş e-postayla yapılır; gemideki yetki yalnız tarihli gemi atamasından gelir.',
    crumb: 'Kullanıcılar',
    table: 'Kullanıcılar',
    search: 'Ad ya da e-posta ara',
    status: 'Durum',
    statusAll: 'Tümü',
    statusActive: 'Etkin',
    statusDisabled: 'Devre dışı',
    new: 'Yeni Kullanıcı',
    loadError: 'Kullanıcılar yüklenemedi.',
    emptyTitle: 'Henüz kullanıcı yok',
    emptyText: 'Ofiste ya da gemide giriş yapacak kişileri ekleyin.',
    noMatch: 'Süzgece uyan kullanıcı yok.',
    pagination: 'Kullanıcı sayfaları',
    showing: '{{total}} kayıttan {{from}}–{{to}}',
    actionsOf: '{{name}} için eylemler',
    columns: {
      name: 'Ad',
      companyRoles: 'Şirket rolleri',
      assignments: 'Gemi atamaları',
      signIn: 'Giriş',
      status: 'Durum',
      actions: 'Eylemler',
    },
    active: 'Etkin',
    disabled: 'Devre dışı',
    admin: 'Yönetici',
    totpSet: 'İki adımlı doğrulama açık',
    totpMissing: 'İki adımlı doğrulama kapalı',
    mustChange: 'Parolasını değiştirmeli',
    none: '—',
    assignment: '{{role}} · {{vessel}}',
    more: '+{{count}} daha',
    action: {
      edit: 'Düzenle',
      changeEmail: 'Giriş e-postasını değiştir',
      resetPassword: 'Parolayı sıfırla',
      resetTotp: 'İki adımlı doğrulamayı kapat',
      disable: 'Devre dışı bırak',
      enable: 'Etkinleştir',
    },
    confirm: {
      password: 'Parolanız',
      passwordHelp: 'Onaylamak için kendi parolanızı girin.',
      cancel: 'Vazgeç',
      createTitle: 'Kullanıcı aç',
      createBody: '{{email}} açılsın mı? Geçici parola açılıştan sonra bir kez gösterilir.',
      create: 'Kullanıcıyı aç',
      disableTitle: 'Kullanıcıyı devre dışı bırak',
      disableBody:
        '{{name}} hemen oturumdan çıkarılır ve bir daha giriş yapamaz. Gemiler değişikliği bir sonraki senkronda alır.',
      disable: 'Devre dışı bırak',
      enableTitle: 'Kullanıcıyı etkinleştir',
      enableBody:
        '{{name}} yeniden giriş yapabilir. Gemiler değişikliği bir sonraki senkronda alır.',
      enable: 'Etkinleştir',
      resetPasswordTitle: 'Parolayı sıfırla',
      resetPasswordBody:
        '{{name}} yeni bir geçici parola alır ve ilk girişte değiştirir. Gemide değiştirilmiş parola geçersiz olur; yeni parola gemide bir sonraki senkronla geçerli olur.',
      resetPassword: 'Parolayı sıfırla',
      resetTotpTitle: 'İki adımlı doğrulamayı kapat',
      resetTotpBody:
        '{{name}} ofise bundan sonra yalnız parolasıyla girer; isterse iki adımlı doğrulamayı Ayarlar ▸ Hesap’tan yeniden açar.',
      resetTotp: 'Doğrulamayı kapat',
    },
    temp: {
      title: 'Geçici parola',
      body: 'Bunu {{name}} kişisine telefonla ya da WhatsApp ile iletin. Yalnız bir kez gösterilir.',
      label: 'Geçici parola',
      copy: 'Parolayı kopyala',
      copied: 'Kopyalandı',
      copyMessage: 'Mesajı kopyala',
      messageCopied: 'Mesaj kopyalandı',
      print: 'Yazdır',
      sheetTitle: 'Felixmate girişi',
      message: {
        hello: 'Merhaba {{name}},',
        ready: 'Felixmate girişiniz hazır.',
        office: 'Ofis: {{url}}',
        ship: 'Gemide: geminin bilgisayarındaki Felixmate uygulamasını kullanın.',
        email: 'Giriş e-postası: {{email}}',
        password: 'Geçici parola: {{password}}',
        change: 'İlk girişte değiştirin.',
      },
      note: 'Kullanıcı ilk girişte değiştirir. Ofiste iki adımlı doğrulama isteğe bağlıdır: Ayarlar ▸ Hesap’tan açılabilir.',
      inactive: '{{name}} devre dışı: hesap etkinleştirilene dek yeni parolayla giriş yapılamaz.',
      done: 'Tamam',
    },
    form: {
      newTitle: 'Yeni Kullanıcı',
      email: 'E-posta',
      emailHelp: 'Gemide ve ofiste giriş için; Felixmate genelinde tekildir.',
      fullName: 'Ad soyad',
      companyRoles: 'Şirket rolleri',
      companyRolesHelp: 'Ofis rolleri, tarihleriyle.',
      assignments: 'Gemi atamaları',
      assignmentsHelp: 'Gemideki yetki yalnız bu tarihli atamalardan gelir.',
      role: 'Rol',
      vessel: 'Gemi',
      from: 'Başlangıç',
      to: 'Bitiş',
      toOpen: 'Süresiz',
      addRole: 'Şirket rolü ekle',
      addAssignment: 'Gemi ataması ekle',
      remove: 'Kaldır',
      save: 'Kaydet',
      cancel: 'Vazgeç',
      next: 'Devam',
      required: 'Zorunlu.',
      // Kontrat bitişi (ADR 0076) [MLC 2006 Std A2.5.1 §2(b)]
      contractEnd: 'Kontrat bitişi',
      contractOpen: 'Girilmedi',
      contractHint: 'Gemide MLC 12 ay sınırı: {{date}}. 60 gün kala uyarı verilir.',
      contractHintEmpty: 'MLC 12 ay sınırını görmek için katılış tarihini girin.',
      contractReason: 'MLC sınırını aşma gerekçesi',
      contractExceeds:
        'Kontrat MLC 12 ay sınırından ({{date}}) sonra bitiyor. Gerekçeyi yazın (ör. bayrak devleti izni).',
      contractReasonRequired: 'Kontrat MLC sınırını aşıyorsa gerekçe zorunludur.',
    },
    drawer: {
      title: 'Kullanıcıyı Düzenle',
      details: 'Bilgiler',
      saveDetails: 'Bilgileri kaydet',
      // Kişinin personel kartı (ADR 0131 §2)
      openCrewCard: 'Personel kartını aç',
      // Kartlı kişi: Sign On / Off işlemleri çekmeceden (ADR 0155 Ek, sahip 2026-11-02 gece)
      assignmentsByCrew:
        'Bu kişinin personel kartı var. Gemi rolü Katılış / Ayrılış işlemleriyle değişir (katılış, rütbe değişikliği, ayrılış); her değişiklik MLC çalışma kaydında tutulur.',
      manageSignOnOff: 'Katılış / Ayrılış’ta yönet',
      noAssignments: 'Henüz gemi ataması yok.',
      crewLoadError: 'Personel kartı okunamadı ({{code}}).',
      saveRoles: 'Şirket rollerini kaydet',
      saveAssignments: 'Gemi atamalarını kaydet',
      // İyimser kilit (ADR 0155 K7)
      assignmentsConflict:
        'Gemi atamaları bu arada değişti (şirket paneli, Sign On / Off ya da başka bir kullanıcı). Hiçbir şey kaydedilmedi. Güncel satırları görmek için kişiyi kapatıp yeniden açın.',
      emailAdminOnly: 'Yöneticinin giriş e-postasını yalnız şirket yöneticisi değiştirebilir.',
      rolesAdminOnly:
        'Bu rollerde yönetici rolü var. Bunları yalnız şirket yöneticisi değiştirebilir.',
      rolesOwn: 'Kendi rollerinizi değiştiremezsiniz; şirket yöneticisinden isteyin.',
      assignmentsOwn: 'Kendi gemi atamalarınızı değiştiremezsiniz; şirket yöneticisinden isteyin.',
      assignmentAdminOnly:
        'Bu atamada yönetici rolü var. Yalnız şirket yöneticisi değiştirebilir ya da kaldırabilir.',
      addRoleNeedsCreate: 'Şirket rolü eklemek için Kullanıcılar ▸ Oluştur yetkisi gerekir.',
      addAssignmentNeedsCreate:
        'Gemi ataması eklemek ya da başka gemiye taşımak için Kullanıcılar ▸ Oluştur yetkisi gerekir.',
      assignmentsAdminScope:
        'Bu kişinin şirket rolü, atandığı gemilerde yönetici yetkisi verir. Gemi atamalarını yalnız şirket yöneticisi değiştirebilir.',
      account: 'Hesap',
      close: 'Kapat',
    },
    menuNote: {
      adminAccount: 'Yönetici hesabını yalnız şirket yöneticisi değiştirebilir.',
      ownAccount: 'Kendi hesabınızı devre dışı bırakamazsınız.',
    },
    saved: '{{name}} kaydedildi.',
    created: '{{name}} açıldı.',
    disabledNotice: '{{name}} devre dışı; oturumları kapatıldı.',
    enabledNotice: '{{name}} etkinleştirildi.',
    totpNotice: '{{name}} için iki adımlı doğrulama kapatıldı.',
    temporaryPasswordDropped_one:
      '{{names}} için geçici parola iptal edildi: hesap artık yönetici rolü taşıyor. Yeni geçici parolayı “{{action}}” ile verin.',
    temporaryPasswordDropped_other:
      '{{names}} için geçici parolalar iptal edildi: hesaplar artık yönetici rolü taşıyor. Yeni geçici parolaları “{{action}}” ile verin.',
    card: {
      title: 'Kullanıcılar',
      text: 'Ofis kullanıcıları ve personel giriş hesapları: hesap açma, giriş e-postasını düzeltme, parola sıfırlama ve iki adımlı doğrulamayı kapatma.',
      open: 'Kullanıcıları aç',
    },
    forbiddenTitle: 'Kullanıcılara erişiminiz yok',
    forbiddenText: 'Kullanıcıları görme yetkisi için şirket yöneticinize başvurun.',
    changeEmail: {
      title: 'Giriş e-postasını değiştir',
      body: '{{name}} kişisinin giriş adresini düzeltin. Kayıtlar ve geçmiş aynı kişide kalır; eski adres serbest kalır. Gemiler değişikliği bir sonraki senkronda alır.',
      current: 'Şu anki giriş e-postası',
      next: 'Yeni giriş e-postası',
      nextHelp: 'Felixmate genelinde tekil olmalı.',
      submit: 'E-postayı değiştir',
      notice:
        '{{name}} kişisinin giriş e-postası {{email}} oldu. Yeni adresle giriş yapmasını söyleyin.',
    },
  },
  matrix: {
    title: 'Yetki ve Onay Matrisleri',
    description:
      'Yetki ve onay matrisleri birlikte, gerekçe ve parolanızla yeni sürüm olarak kaydedilir. Önceki kayıtlar girildikleri sürümle değerlendirilir.',
    crumb: 'Matrisler',
    tabs: { permissions: 'Yetkiler', approvals: 'Onaylar', history: 'Sürüm geçmişi' },
    loadError: 'Matrisler yüklenemedi.',
    readOnly: 'Matrisleri görebilirsiniz. Değiştirmek için ayar düzenleme yetkisi gerekir.',
    modules: 'Modüller',
    moduleGranted_one: '{{count}} yetki',
    moduleGranted_other: '{{count}} yetki',
    grid: '{{module}} yetkileri',
    gridHelp:
      'Kapsamı değiştirmek için hücreye tıklayın ya da ok tuşları ve Boşluk tuşunu kullanın: yok → kendi → gemi → filo.',
    role: 'Rol',
    subject: 'Konu',
    subjectAll: 'Bütün formlar',
    addSubject: 'Konu ekle',
    removeSubject: 'Konuyu kaldır',
    subjectHelp:
      'Konu satırı bir form kategorisi ya da tek bir form için modül satırının yerine geçer; "yok" onu kapatır.',
    locked: 'Verilemez: {{source}}',
    justified: 'Gerekçe ister',
    cellLabel: '{{role}}, {{action}}: {{scope}}',
    scope: { none: 'Yok', own: 'Kendi', vessel: 'Gemi', fleet: 'Filo' },
    scopeShort: { none: '—', own: 'Kendi', vessel: 'Gemi', fleet: 'Filo' },
    legend: 'Kapsam açıklaması',
    actions: {
      view: 'Görüntüle',
      create: 'Oluştur',
      edit: 'Düzenle',
      submit: 'Gönder',
      approve: 'Onayla',
      return: 'İade et',
      void: 'İptal et',
      export: 'Dışa aktar',
    },
    moduleNames: {
      ism_forms: 'ISM formları',
      equipment: 'Ekipman',
      maintenance_program: 'Bakım programı',
      work_records: 'İş kayıtları',
      running_hours: 'Çalışma saatleri',
      postponements: 'Ertelemeler',
      defects: 'Arızalar',
      work_orders: 'İş emirleri',
      vessel_certificates: 'Gemi sertifikaları',
      reports: 'Raporlar',
      users: 'Kullanıcılar',
      devices: 'Cihazlar',
      settings: 'Ayarlar',
      crew_certificates: 'Personel belgeleri',
      rest_hours: 'Dinlenme saatleri',
      voyage: 'Sefer',
      documents: 'Dokümanlar',
      document_acknowledgements: 'Doküman teyitleri',
      document_tree: 'Doküman ağacı',
      materials: 'Malzemeler',
      stock: 'Stok',
      purchasing: 'Satın alma',
      hse_reports: 'HSE raporları',
      daily_works: 'Günlük iş planı',
      daily_reports: 'Günlük raporlar',
      risk_assessments: 'Risk değerlendirmeleri',
      payroll: 'Payroll',
      cash: 'Kaptan kasası ve CTM',
      provisions: 'Kumanya',
      fuel: 'Yakıt yönetimi',
      mrv: 'MRV / ETS',
      drills: 'Tatbikat yönetimi',
      drydock: 'Havuzlama ve havuz öncesi denetim',
      crew_medical: 'Personel sağlık bilgisi',
      hse_medical: 'HSE sağlık bilgisi',
      crew_notes: 'Personel ofis notları',
      company_nonconformities: 'Şirket uygunsuzlukları',
      ship_clock: 'Gemi saati kaydı',
      ra_library: 'Risk değerlendirmesi kütüphanesi',
      ra_operations: 'Operasyonel risk doğrulaması',
    },
    recordTypes: {
      rest_hours_month: 'Aylık dinlenme saatleri',
      voyage_report: 'Sefer raporu',
      requisition: 'Malzeme talebi',
      document_revision: 'Doküman revizyonu',
      ism_form: 'ISM formu',
      vessel_document_revision: 'Gemi dokümanı revizyonu',
      pms_work_record: 'PMS iş kaydı',
      postponement_request: 'Erteleme talebi',
      maintenance_interval_change: 'Bakım aralığı değişikliği',
      defect_report: 'Arıza raporu',
      breakdown_report: 'Hasar raporu',
      hse_non_conformity: 'Uygunsuzluk kapanışı',
      hse_deficiency: 'Eksiklik kapanışı',
      hse_near_miss: 'Ramak kala kapanışı',
      hse_accident: 'Kaza kapanışı',
      daily_report: 'Günlük rapor',
      risk_assessment: 'Risk değerlendirmesi',
      ctm_request: 'Kaptana nakit talebi (CTM)',
    },
    summary: {
      title: 'Bu Sürüm',
      base: 'Temel',
      baseValue: 'v{{version}} · {{at}}',
      by: 'Kaydeden',
      system: 'Felixmate (varsayılan)',
      reason: 'Gerekçe',
      changes: 'Kaydedilmemiş değişiklikler',
      changesValue_one: '{{count}} değişiklik',
      changesValue_other: '{{count}} değişiklik',
      noChanges: 'Değişiklik yok',
      save: 'Yeni Sürümü Kaydet',
      discard: 'Değişiklikleri at',
      export: "Excel'e aktar",
      import: "Excel'den al",
    },
    justification: {
      title: 'Gerekçe gerekli',
      body: '{{role}} PMS sorumlusu rol değil. Bakım programında "{{action}}" yetkisi gerekçe ister [UR Z20 1.3.3].',
      label: 'Gerekçe',
      save: 'Yetkiyi ver',
      cancel: 'Vazgeç',
    },
    subjectDialog: {
      title: 'Konu ekle',
      kind: 'Konu türü',
      folder: 'Form kategorisi',
      document: 'Tek form',
      folderLabel: 'Kategori',
      documentLabel: 'Form',
      companyForm: 'Şirket formu',
      add: 'Ekle',
      cancel: 'Vazgeç',
      empty: 'Seçilecek kategori ya da form yok.',
    },
    rules: {
      table: 'Onay kuralları',
      help: 'Kural kayıt türüne, ISM form kategorisine ve kaydı giren role göre seçilir; en çok iki adım.',
      add: 'Kural Ekle',
      edit: 'Düzenle',
      remove: 'Kaldır',
      actionsOf: '{{index}}. kural için eylemler',
      empty: 'Onay kuralı yok.',
      columns: {
        recordType: 'Kayıt türü',
        subject: 'Konu',
        enteredBy: 'Giren',
        step1: '1. adım',
        step2: '2. adım',
        amount: 'Tutar kademesi',
        deadline: 'Süre',
        selfApproval: 'Öz onay',
        actions: 'Eylemler',
      },
      any: 'Her rol',
      office: 'Ofis',
      noStep: 'Onay yok',
      hours: '{{count}} sa',
      from: '{{amount}} ve üstü',
      to: '{{amount}} altı',
      dialog: {
        newTitle: 'Onay Kuralı Ekle',
        editTitle: 'Onay Kuralını Düzenle',
        recordType: 'Kayıt türü',
        subject: 'Konu (yalnız ISM formları)',
        subjectNone: 'Bütün ISM formları',
        enteredBy: 'Giren',
        anyRole: 'Her rol',
        step1: '1. adım onaycısı',
        step2: '2. adım onaycısı',
        noStep: 'Yok',
        office: 'Ofiste karar verilir',
        minAmount: 'Tutar (dahil)',
        maxAmount: 'Tutar (altı)',
        deadline: 'Süre (saat)',
        selfApproval: 'Öz onay gerekçesi',
        selfApprovalHelp: 'Yalnız aynı kişi girip onaylayabilecekse; raporlarda görünür.',
        save: 'Kaydet',
        cancel: 'Vazgeç',
      },
    },
    history: {
      table: 'Matris sürümleri',
      columns: {
        version: 'Sürüm',
        at: 'Kaydedildi',
        by: 'Kaydeden',
        reason: 'Gerekçe',
        compare: 'Karşılaştır',
      },
      compare: 'Öncekiyle karşılaştır',
      from: 'Başlangıç sürümü',
      to: 'Bitiş sürümü',
      show: 'Farkları göster',
    },
    diff: {
      title: 'Farklar',
      permissions: 'Yetki hücreleri',
      approvals: 'Onay kuralları',
      justifications: 'Gerekçeler',
      added: 'Eklendi',
      removed: 'Kaldırıldı',
      changed: 'Değişti',
      none: 'Fark yok.',
      change: 'Değişiklik',
      fromTo: '{{from}} → {{to}}',
      summary: '{{added}} eklendi, {{removed}} kaldırıldı, {{changed}} değişti',
    },
    save: {
      title: 'Yeni matris sürümünü kaydet',
      body: 'İki matris v{{version}} olarak kaydedilir. Gemiler bir sonraki senkronda alır.',
      reason: 'Değişikliğin gerekçesi',
      password: 'Parolanız',
      confirm: 'Sürümü Kaydet',
      cancel: 'Vazgeç',
      conflict:
        'Siz düzenlerken başka biri daha yeni bir sürüm kaydetti. Onu yükleyin ve değişikliklerinizi yeniden uygulayın.',
      reload: 'Son sürümü yükle',
      saved: 'v{{version}} kaydedildi.',
      temporaryPasswordDropped_one:
        '{{count}} kişinin geçici parolası iptal edildi: rolü artık yönetici rolü. Yenisini Ayarlar ▸ Kullanıcılar’da “{{action}}” ile verin.',
      temporaryPasswordDropped_other:
        '{{count}} kişinin geçici parolası iptal edildi: rolleri artık yönetici rolü. Yenilerini Ayarlar ▸ Kullanıcılar’da “{{action}}” ile verin.',
    },
    import: {
      title: "Excel'den al",
      choose: 'Bu ekrandan dışa aktarılmış bir .xlsx dosyası seçin.',
      file: 'Excel dosyası',
      errors: 'Dosyada hatalar var; hiçbir şey değişmedi.',
      errorRow: '{{sheet}} {{row}}. satır, {{field}}: {{code}}',
      problem: 'Alınan matris bir kuralı bozuyor: {{code}}.',
      ready:
        'Farkları inceleyin. Alınan matris taslağınızın yerine geçer; gerekçeyle yeni sürüm olarak kaydedin.',
      use: 'Alınan matrisi kullan',
      cancel: 'Vazgeç',
      sheets: {
        permissions: 'Yetkiler',
        approvals: 'Onaylar',
        justifications: 'Gerekçeler',
      },
    },
  },
  errors: {
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    UNAUTHENTICATED: 'Oturumunuz kapandı. Yeniden giriş yapın.',
    NETWORK: 'Sunucuya bağlanılamıyor.',
    VALIDATION_FAILED: 'Bazı alanlar geçersiz.',
    REAUTHENTICATION_REQUIRED: 'Parolanızı girin.',
    INVALID_PASSWORD: 'Parola doğru değil.',
    TOO_MANY_ATTEMPTS: 'Çok fazla deneme. 15 dakika sonra yeniden deneyin.',
    ROW_VERSION_CONFLICT: 'Bu kaydı başka biri değiştirdi. Yenileyip yeniden deneyin.',
    NOT_FOUND: 'Kayıt bulunamadı.',
    DEFAULT_ROLE_LOCKED: 'Varsayılan rollerin adı ve türü değiştirilemez, emekli edilemez.',
    DUPLICATE_ROLE_NAME: 'Bu adla bir rol zaten var.',
    ROLE_IN_USE: 'Rol güncel matriste ya da bir atamada kullanılıyor.',
    ROLE_NAME_REQUIRED: 'Rol adını girin.',
    ROLE_NAME_TOO_LONG: 'Rol adı çok uzun (en çok 80 karakter).',
    ROLE_NAME_INVALID: 'Rol adı ";" içeremez.',
    ROLE_DESCRIPTION_TOO_LONG: 'Açıklama çok uzun.',
    ROLE_KIND_MISMATCH:
      'Gemi rütbesi şirket rolü olarak verilemez. Bir ofis ünvanı seçin ya da Roller’de rolün türünü değiştirin.',
    EMAIL_TAKEN: 'Bu e-posta adresi Felixmate’da zaten kullanılıyor.',
    INVALID_EMAIL: 'Geçerli bir e-posta adresi girin.',
    ASSIGNMENT_OVERLAP: 'Aynı gemide (ya da aynı şirket rolünde) iki atama çakışıyor.',
    INVALID_DATE_RANGE: 'Bitiş tarihi başlangıçtan önce.',
    CONTRACT_EXCEEDS_MLC:
      'Kontrat bitişi gemide MLC 12 ay sınırından sonra. Kaydetmek için gerekçe girin.',
    INVALID_LOCAL_DATE: 'Geçerli bir tarih girin.',
    UNKNOWN_ROLE: 'Rol yok.',
    UNKNOWN_VESSEL: 'Gemi yok.',
    LAST_ADMIN: 'Bugün ve sonraki her gün en az bir etkin yönetici kalmalı.',
    LAST_ADMIN_NO_SIGN_IN:
      'Giriş yapabilen yönetici kalmazdı: öteki yöneticilerin giriş hesabı ya da parolası yok (kişi yönetici olunca geçici parolası iptal edilir). Önce birine yeni parola verin (giriş hesabı yoksa Personel ▸ Giriş Hesapları’nda hesap açın), sonra yeniden deneyin.',
    LAST_ADMIN_SELF: 'Kendi hesabınızı devre dışı bırakamazsınız.',
    INVALID_TRANSITION: 'Hesap zaten bu durumda.',
    TOTP_NOT_ENROLLED: 'Bu kullanıcının iki adımlı doğrulaması zaten kapalı.',
    MATRIX_VERSION_CONFLICT: 'Bu arada daha yeni bir sürüm kaydedildi.',
    MATRIX_LOCKOUT:
      'Bugünden başlayarak her gün en az bir etkin kullanıcı "Ayarlar – Düzenle (Filo)" yetkisini korumalı; yoksa kurulumu kimse değiştiremezdi.',
    MATRIX_LOCKOUT_NO_SIGN_IN:
      '"Ayarlar – Düzenle (Filo)" yetkisini giriş yapabilen bir yönetici korumazdı: yetkiyi koruyacak kişilerin giriş hesabı ya da parolası yok (kayıt, yönetici olan kişilerin geçici parolasını iptal eder). Bu sürümde kendi yetkinizi koruyun, onlara yeni parola verin (giriş hesabı yoksa Personel ▸ Giriş Hesapları’nda hesap açın), sonra değişikliği yeniden kaydedin.',
    NO_CHANGE: 'Hiçbir şey değişmedi.',
    REASON_REQUIRED: 'Gerekçe girin (en az 5 karakter).',
    REASON_TOO_LONG: 'Gerekçe çok uzun.',
    NON_GRANTABLE_CELL: 'Verilemeyen bir hücre işaretli.',
    JUSTIFICATION_REQUIRED: 'Bakım programı yetkisi gerekçe ister.',
    TOO_MANY_APPROVAL_STEPS: 'Onay kuralı en çok iki adımlıdır.',
    DUPLICATE_RULE: 'İki kural aynı kayıt türüne ve rollere uyuyor.',
    OVERLAPPING_AMOUNT_BANDS: 'İki tutar kademesi çakışıyor.',
    AMOUNT_BAND_GAP: '"Her rol" tutar kademeleri bütün tutarları kapsamalı.',
    INVALID_AMOUNT_BAND: 'Tutar kademesi geçersiz.',
    INVALID_DEADLINE: 'Süre pozitif bir saat olmalı.',
    INVALID_OFFICE_STEP: 'Ofis adımının onaycısı olmalı.',
    FORM_OFFICE_STEP_NOT_ALLOWED: 'ISM formları ofis onayına gitmez. Ofis adımını kaldırın.',
    SELF_APPROVAL_REASON_REQUIRED: 'Öz onay gerekçesini girin.',
    ENTERED_BY_REQUIRED: 'Kaydı kimin girdiğini seçin.',
    SUBJECT_ONLY_FOR_FORMS: 'Konu yalnız ISM formlarında olabilir.',
    INVALID_SUBJECT: 'Konu geçersiz.',
    UNKNOWN_RECORD_TYPE: 'Bilinmeyen kayıt türü.',
    DUPLICATE_CELL: 'Bir hücre iki kez yazılmış.',
    INVALID_FILE: 'Dosya Excel çalışma kitabı olarak okunamadı.',
    SHEET_MISSING: 'Çalışma kitabında Permissions ya da Approvals sayfası yok.',
    INVALID_HEADER: 'Sütun başlıkları değiştirilmiş; yeni bir dosya dışa aktarın.',
    UNSUPPORTED_FILE_TYPE: 'Yalnız makro ve etkin içerik taşımayan .xlsx dosyaları kabul edilir.',
    FILE_TOO_LARGE: 'Dosya çok büyük (en çok 10 MB).',
    FILE_TYPE_MISMATCH: 'Dosyanın içeriği .xlsx ile uyuşmuyor.',
    GENERIC: 'İşlem yapılamadı. Yeniden deneyin.',
  },
};
